import { ArticleEmailSubscribe } from "~/components/ArticleEmailSubscribe";
import { apiToPullQuoteProps } from "~/components/ArticlePullQuote/apiToPullQuoteProps";
import { ArticlePullQuote } from "~/components/ArticlePullQuote/ArticlePullQuote";
import { ArticleTagSubscriptions } from "~/components/ArticleTagSubscriptions";
import {
  apiToEvervizChartProps,
  EvervizChart,
} from "~/components/EvervizChart";
import { FloatingImageWithText } from "~/components/FloatingImageWithText";
import { apiToFloatingImageWithTextProps } from "~/components/FloatingImageWithText/apiToFloatingImageWithTextProps";
import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import {
  TabbedCharts,
  TabbedChartsProps,
} from "~/components/TabbedCharts/TabbedCharts";
import { YouTubeEmbedBlock } from "~/components/YouTubeEmbedBlock/YouTubeEmbedBlock";
import { PostContentBlock } from "~/types/asgard/PostContentBlock";
import { OptionData } from "~/ui-library/components/Select";
import { ArticleContentBlock } from "../ArticleContentBlock";
import { CorrectionBlockCard } from "../CorrectionBlockCard";
import {
  apiToFullWidthImageBlockProps,
  FullWidthImageBlock,
} from "../FullWidthImageBlock";
import {
  apiToRelatedStoryCardProps,
  RelatedStoryCard,
} from "../RelatedStoryCard";
import {
  isCorrectionBlockContentBlock,
  isDatavizBlockContentBlock,
  isEmailSubscribeBlock,
  isFullWidthImageContentBlock,
  isImageWithFloatedTextContentBlock,
  isPlainContentBlockContentBlock,
  isPullQuoteContentBlock,
  isRelatedStoryContentBlock,
  isTabbedChartsContentBlock,
  isTagSubscriptionBlock,
  isYoutubeEmbedBlock,
} from "./content-block-type-guards";

export type RenderProps = {
  children: React.ReactNode;
};

export type PostContentBlocksProps = {
  maxColumnWidth?: "medium" | "memo";
  postContentBlocks: PostContentBlock[];
  /** Unique post id used for keys within the .map() below */
  postId: number;
};

export const PostContentBlocks: React.FC<PostContentBlocksProps> = ({
  maxColumnWidth = "memo",
  postContentBlocks,
  postId,
}): JSX.Element | null => {
  if (!postContentBlocks?.length) {
    return null;
  }
  const contentBlockComponents = postContentBlocks.map(
    (contentBlock: PostContentBlock, index: number) => {
      if (isCorrectionBlockContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className="my-l text-body-large leading-body lg:my-m"
            key={`${postId}-${index}`}
          >
            <CorrectionBlockCard bodyHTML={contentBlock.correction_text} />
          </LandingPageContent>
        );
      }

      if (isDatavizBlockContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <EvervizChart {...apiToEvervizChartProps(contentBlock)} />
          </LandingPageContent>
        );
      }
      if (isTabbedChartsContentBlock(contentBlock)) {
        const { headline, subhead, title, tab: tabs } = contentBlock;

        const selectOptions = tabs.map<OptionData>(({ tab }, idx) => ({
          id: tab.title,
          label: tab.title,
          index: idx,
        }));

        const tabsProps = tabs.map<TabbedChartsProps["tabs"][number]>(
          ({ tab }) => {
            if (tab.inline_chart) {
              return {
                label: tab.title,
                tabContent: (
                  <EvervizChart {...tab.inline_chart} showHeader={false} />
                ),
              };
            }

            return {
              label: tab.title,
              tabContent: (
                <EvervizChart
                  chartId={tab.chart_post.chart.chartId}
                  injectId={tab.chart_post.chart.injectId}
                  footnote={tab.chart_post.footnote}
                  logo={tab.chart_post.logo}
                  methodology={tab.chart_post.methodology}
                  image={tab.chart_post.chart.chartImage?.source}
                  altText={tab.chart_post.chart.chartImage?.altText}
                  showHeader={false}
                />
              ),
            };
          }
        );

        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <TabbedCharts
              title={title}
              headline={headline}
              subhead={subhead}
              options={selectOptions}
              tabs={tabsProps}
            />
          </LandingPageContent>
        );
      }

      if (isFullWidthImageContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <FullWidthImageBlock
              {...apiToFullWidthImageBlockProps(contentBlock)}
            />
          </LandingPageContent>
        );
      }

      if (isImageWithFloatedTextContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <FloatingImageWithText
              {...apiToFloatingImageWithTextProps(contentBlock)}
            />
          </LandingPageContent>
        );
      }

      if (isPlainContentBlockContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className="mb-l mt-xl text-body-large leading-body"
            key={`${postId}-${index}`}
          >
            <ArticleContentBlock contentHTML={contentBlock.plain_content} />
          </LandingPageContent>
        );
      }

      if (isPullQuoteContentBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <ArticlePullQuote {...apiToPullQuoteProps(contentBlock)} />
          </LandingPageContent>
        );
      }

      if (isRelatedStoryContentBlock(contentBlock)) {
        const props = apiToRelatedStoryCardProps(contentBlock);
        return props.href ? (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <RelatedStoryCard {...props} />
          </LandingPageContent>
        ) : null;
      }

      if (isEmailSubscribeBlock(contentBlock)) {
        const { description, kicker, title } = contentBlock;
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <ArticleEmailSubscribe
              buttonText="Subscribe"
              description={description}
              defaultInputValue={""}
              kicker={kicker}
              title={title}
            />
          </LandingPageContent>
        );
      }

      if (isTagSubscriptionBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <ArticleTagSubscriptions tags={contentBlock.tags} />
          </LandingPageContent>
        );
      }

      if (isYoutubeEmbedBlock(contentBlock)) {
        return (
          <LandingPageContent
            maxWidth={maxColumnWidth}
            className={"my-l text-body-large leading-body lg:my-m"}
            key={`${postId}-${index}`}
          >
            <YouTubeEmbedBlock
              src={contentBlock.video_url}
              title={`YouTube video player ${index + 1}`}
              key={`${contentBlock.acf_fc_layout}-${index}`}
            />
          </LandingPageContent>
        );
      }
      return null;
    }
  );

  return <>{contentBlockComponents}</>;
};
